/* eslint-disable max-lines */
/* eslint-disable max-depth */
import { AgGridAngular } from '@ag-grid-community/angular';
import {
  CellClassParams,
  CellClickedEvent,
  ColDef,
  ColGroupDef,
  ExcelExportParams,
  GetQuickFilterTextParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ITooltipParams,
  ProcessCellForExportParams,
  RowNode,
  ValueFormatterParams,
} from '@ag-grid-community/core';
import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
  runInInjectionContext,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { Chart, ChartDataset } from 'chart.js';
import dayjs from 'dayjs';
import { BehaviorSubject, combineLatest, firstValueFrom, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';

import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';
import { VariationStatusComponent } from '@features/variation-status/variation-status.component';

import { AgCellWrapperComponent } from '@shared/ag-components/ag-cell-wrapper/ag-cell-wrapper.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { InputComponent } from '@shared/components/input/input.component';
import { VendorDropdownComponent } from '@shared/components/vendor-dropdown/vendor-dropdown.component';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { TableConstants } from '@shared/constants/table.constants';
import { NoopValueAccessorDirective } from '@shared/directives/noop-value-accessor.directive';
import { StickyGridDirective } from '@shared/directives/sticky-grid/sticky-grid.directive';
import { ChangeOrderSharedService } from '@shared/services/change-order-shared.service';
import {
  BudgetType,
  BudgetVersion,
  ChangeOrder,
  ChangeOrderStatus,
  Currency,
  EntityType,
  GqlService,
  listBudgetVersionExpensesQuery,
  Organization,
  PermissionType,
  WorkflowStep,
} from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { AuthService } from '@shared/store/auth/auth.service';
import { BudgetCurrencyType } from '@shared/store/budget-currency/budget-currency.model';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { OrganizationService } from '@shared/store/organization/organization.service';
import { OrganizationStore } from '@shared/store/organization/organization.store';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { WorkflowService } from '@shared/store/workflow/workflow.service';
import { AuxExcelStyleKeys, AuxExcelStyles, batchPromises, GetExcelStyle } from '@shared/utils';
import { injectExecutableByRoute } from '@shared/utils/route.utils';
import { Utils } from '@shared/utils/utils';

import { ChangeOrderActionsComponent } from './change-order-actions.component';
import { ChangeOrderStatusComponent } from './change-order-status.component';
import {
  ChangeOrderUploadComponent,
  ChangeOrderUploadData,
} from './change-order-upload/change-order-upload.component';
import { ChangeOrderQuery } from './state/change-order.query';
import { ChangeOrderService } from './state/change-order.service';
import { agChangeOrderCurrencyFormatter } from './utils/change-order-formatters.utils';
import { ConfirmationWithReasonModalComponent } from '../../components/confirmation-with-reason.component';

enum CellSize {
  large = 125,
  medium = 100,
  small = 75,
  xLarge = 150,
}

interface BvExpenseData extends Partial<BudgetVersion> {
  budget_short_type: 'BASE' | 'CO' | 'LRE' | undefined;
  expense_data: listBudgetVersionExpensesQuery[] | null;
}

interface ChangeOrderGridData {
  approved_by: string;
  approved_date: string;
  budget_total: unknown;
  co_id: string;
  co_number: string;
  created_by: string;
  date_created: string;
  status: string;
  total_cost_impact: number;
  vendor_currency: Currency;
  vendor_id: string;
  vendor_name: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    InputComponent,
    FormsModule,
    NoopValueAccessorDirective,
    VendorDropdownComponent,
    ReactiveFormsModule,
    IconComponent,
    ExportExcelButtonComponent,
    AgGridAngular,
    StickyGridDirective,
    AsyncPipe,
  ],
  selector: 'aux-change-order',
  styles: [
    `
      :host {
        display: block;
      }
      ::ng-deep .change-order-table .ag-header-group-cell {
        background: var(--aux-gray-light);
        border: 0.5px solid var(--aux-gray-dark);
        border-bottom: 0;
      }
      ::ng-deep .change-order-table .open {
        background: var(--aux-gray-dark);
        color: var(--aux-black);
      }
      ::ng-deep .change-order-table .ag-header-row,
      ::ng-deep .change-order-table .ag-header-cell {
        font-size: 1rem;
        overflow: unset;
      }
      ::ng-deep .change-order-table .ag-header-cell-text,
      ::ng-deep .change-order-table .ag-row {
        font-size: 1rem;
        color: var(--aux-black);
      }
      ::ng-deep .change-order-table .ag-row {
        background: white !important;
      }
      ::ng-deep .change-order-table .ag-header-cell {
        text-align: center;
      }
      ::ng-deep .change-order-table .ag-header-cell-label {
        justify-content: center;
      }
      ::ng-deep .change-order-table .ag-header-cell-text {
        white-space: normal !important;
      }
      ::ng-deep .change-order-table .text-elypsis {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      ::ng-deep .change-order-table .net_accruals_header {
        padding: 0;
      }
      ::ng-deep .change-order-table .ag-cell.grid-cell {
        margin: 0 !important;
        padding-left: 5px !important;
        padding-right: 5px !important;
      }
      ::ng-deep .change-order-table .status-cell {
        display: flex;
        align-items: center;
      }
      ::ng-deep .change-order-table .action-cell {
        display: flex;
        align-items: center;
        justify-content: center;
      }
    `,
  ],
  templateUrl: './change-order.component.html',
})
export class ChangeOrderComponent implements AfterViewInit {
  changeOrderQuery = inject(ChangeOrderQuery);
  organizationQuery = inject(OrganizationQuery);

  private authQuery = inject(AuthQuery);
  private authService = inject(AuthService);
  private changeOrderService = inject(ChangeOrderService);
  private changeOrderSharedService = inject(ChangeOrderSharedService);
  private gqlService = inject(GqlService);
  private injector = inject(Injector);
  private launchDarklyService = inject(LaunchDarklyService);
  private mainQuery = inject(MainQuery);
  private organizationService = inject(OrganizationService);
  private organizationStore = inject(OrganizationStore);
  private overlayService = inject(OverlayService);
  private router = inject(Router);
  private workflowQuery = inject(WorkflowQuery);
  private workflowService = inject(WorkflowService);

  changeOrders: Partial<ChangeOrder>[] = [];
  organizations: { [id: string]: Partial<Organization> } = {};
  totalCosts: { total: number; variance: number } = { total: 0, variance: 0 };

  gridData$ = combineLatest([
    this.changeOrderQuery.selectAll(),
    this.organizationQuery.selectAll({ asObject: true }),
  ]).pipe(
    map(([changeOrders, organizations]) => {
      this.organizations = organizations as { [id: string]: Partial<Organization> };
      this.changeOrders = changeOrders as Partial<ChangeOrder>[];

      if (this.showCOChart$.getValue()) {
        void this.drawChart(
          organizations as { [id: string]: Partial<Organization> },
          changeOrders as Partial<ChangeOrder>[]
        );
      }

      if (changeOrders.length > 0 && organizations) {
        const firstCurrency = organizations[changeOrders[0].organization_id]?.currency;

        this.isAllCosSameCurrency = changeOrders.every((co) => {
          const comparedCurrency = organizations[co.organization_id]?.currency;

          return firstCurrency && comparedCurrency && comparedCurrency === firstCurrency;
        });

        if (this.isAllCosSameCurrency) {
          this.allOrgCurrency = firstCurrency || Currency.USD;
        }
      }

      this.totalCosts.total = 0;
      this.totalCosts.variance = 0;

      return changeOrders.map((changeOrder) => {
        const budget_version = this.organizationQuery.getBudgetVersion(
          changeOrder.organization_id,
          BudgetType.BUDGET_CHANGE_ORDER,
          changeOrder.id,
          EntityType.CHANGE_ORDER
        );
        const total_cost_impact = budget_version?.total_budget_amount;
        const variance = changeOrder.budget_total?.variance;

        this.totalCosts.total += total_cost_impact || 0;
        this.totalCosts.variance += variance || 0;

        return {
          approved_by: changeOrder.approvals ? changeOrder.approvals[0]?.aux_user_id : null,
          approved_date: changeOrder.approvals ? changeOrder.approvals[0]?.approval_time : null,
          budget_total: changeOrder.budget_total,
          co_id: changeOrder.id,
          co_number: changeOrder.change_order_no,
          created_by: changeOrder.created_by,
          date_created: changeOrder.create_date,
          status: changeOrder.change_order_status,
          total_cost_impact: total_cost_impact,
          vendor_currency: changeOrder.organization_id
            ? organizations[changeOrder.organization_id]?.currency
            : null,
          vendor_id: changeOrder.organization_id,
          vendor_name: organizations[changeOrder.organization_id]?.name,
        } as ChangeOrderGridData;
      });
    })
  );
  loading$ = combineLatest([
    this.changeOrderQuery.selectLoading(),
    this.organizationQuery.selectLoading(),
  ]).pipe(map((arr) => arr.some((x) => x)));

  allOrgCurrency: Currency = Currency.USD;
  coChartLoading$ = new BehaviorSubject(false);
  excelOptions = {
    columnWidth(params) {
      switch (params.column?.getId()) {
        case 'co_number':
        case 'vendor_name':
          return 280;
        default:
          return 150;
      }
    },
    fileName: 'auxilius-change-order.xlsx',
    processCellCallback: (params: ProcessCellForExportParams): string => {
      if (
        params.column.getColId() === 'approved_date' ||
        params.column.getColId() === 'date_created'
      ) {
        return dayjs(params.value).format('YYYY-MM-DD');
      } else if (
        params.column.getColId() === 'approved_by' ||
        params.column.getColId() === 'created_by'
      ) {
        return this.changeOrderSharedService.userFormatter(params.value);
      } else if (params.column.getColId() === 'status') {
        switch (params.value as ChangeOrderStatus) {
          case ChangeOrderStatus.STATUS_APPROVED:
            return 'Approved';
          case ChangeOrderStatus.STATUS_DECLINED:
            return 'Declined';
          case ChangeOrderStatus.STATUS_PENDING_APPROVAL:
            return 'Pending Approval';
          case ChangeOrderStatus.STATUS_PENDING_REVIEW:
            return 'Pending Review';
          default:
            return Utils.zeroHyphen;
        }
      }

      return params.value;
    },
    sheetName: 'Change order',
    shouldRowBeSkipped(params) {
      return !params.node?.data;
    },
  } as ExcelExportParams;
  gridAPI!: GridApi;
  gridAPI$ = new ReplaySubject<GridApi>(1);
  isChangeOrdersWorkflowLocked = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_CHANGE_ORDERS
  );
  selectedBudgetCurrencyType$ = new BehaviorSubject<BudgetCurrencyType>(BudgetCurrencyType.VENDOR);
  userHasDownloadChangeOrderPermission$ = new BehaviorSubject(false);

  getCellClassForNumbers =
    () =>
    (params: CellClassParams): string[] => {
      const classes: string[] = [];

      if (
        params?.node?.data?.vendor_currency &&
        this.selectedBudgetCurrencyType$.getValue() === BudgetCurrencyType.VENDOR
      ) {
        classes.push(`budgetCost${params?.node?.data?.vendor_currency}`);
      } else {
        classes.push(`budgetCostUSD`);
      }

      classes.push(TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT);

      return classes;
    };

  gridOptions$ = new BehaviorSubject({
    columnDefs: [
      {
        field: 'co_id',
        headerName: 'co_id',
        hide: true,
      },
      {
        field: 'vendor_id',
        filter: true,
        headerName: 'vendor_id',
        hide: true,
      },
      {
        cellClass: ['grid-cell', 'action-cell'],
        cellRenderer: ChangeOrderActionsComponent,
        cellRendererParams: {
          deleteClickFN: ({ rowNode }: { rowNode: RowNode }) => {
            void this.removeChangeOrder(rowNode);
          },
          downloadClickFN: ({ rowNode }: { rowNode: RowNode }) => {
            void this.downloadChangeOrder(rowNode);
          },
          downloadPermission$: this.userHasDownloadChangeOrderPermission$,
          isAdmin: this.authQuery.isAuxAdmin(),
          isChangeOrdersWorkflowLocked: this.isChangeOrdersWorkflowLocked,
        },
        editable: false,
        field: 'actions',
        headerName: '',
        maxWidth: 90,
        minWidth: 80,
      },
      {
        cellClass: [
          'aux-link',
          'cursor-pointer',
          'ag-cell-align-left',
          'text-format',
          'cell-left',
          'text-elypsis',
        ],
        field: 'co_number',
        headerName: 'Change Order #',
        onCellClicked: (event) => this.goToCODetail(event),
        resizable: true,
        tooltipField: 'co_number',
        type: 'string',
      },
      {
        cellClass: ['grid-cell', 'ag-cell-align-left', 'text-elypsis'],
        field: 'vendor_name',
        headerName: 'Vendor Contract',
        resizable: true,
        tooltipField: 'vendor_name',
      },
      {
        cellClass: this.getCellClassForNumbers(),
        field: 'total_cost_impact',
        headerName: 'Total',
        minWidth: 150,
        resizable: true,
        suppressSizeToFit: true,
        valueFormatter: agChangeOrderCurrencyFormatter(this.selectedBudgetCurrencyType$.getValue()),
        width: 150,
      },
      {
        cellClass: this.getCellClassForNumbers(),
        cellRenderer: VariationStatusComponent,
        field: `budget_total.variance`,
        headerName: 'Variance',
        minWidth: CellSize.xLarge,
        resizable: true,
        suppressSizeToFit: true,
        valueFormatter: agChangeOrderCurrencyFormatter(this.selectedBudgetCurrencyType$.getValue()),
        width: CellSize.xLarge,
      },
      {
        cellClass: ['grid-cell', 'status-cell'],
        cellRenderer: ChangeOrderStatusComponent,
        field: 'status',
        getQuickFilterText: (params: GetQuickFilterTextParams) => {
          switch (params.value as ChangeOrderStatus) {
            case ChangeOrderStatus.STATUS_APPROVED:
              return 'Approved';
            case ChangeOrderStatus.STATUS_DECLINED:
              return 'Declined';
            case ChangeOrderStatus.STATUS_PENDING_APPROVAL:
              return 'Pending Approval';
            case ChangeOrderStatus.STATUS_PENDING_REVIEW:
              return 'Pending Review';
            default:
              return Utils.zeroHyphen;
          }
        },
        headerName: 'Status',
        minWidth: CellSize.xLarge,
        resizable: true,
        width: CellSize.xLarge,
      },
      {
        cellClass: ['grid-cell', 'ag-cell-align-left'],
        field: 'approved_date',
        getQuickFilterText: Utils.agDateFormatter,
        headerName: 'Date Approved',
        minWidth: 135,
        resizable: true,
        type: 'date',
        valueFormatter: Utils.agDateFormatter,
        width: 135,
      },
      {
        cellClass: ['grid-cell', 'ag-cell-align-left', 'text-elypsis'],
        field: 'approved_by',
        getQuickFilterText: (params: GetQuickFilterTextParams) =>
          this.changeOrderSharedService.userFormatter(params.value),
        headerName: 'Approved By',
        resizable: true,
        tooltipValueGetter: (params: ITooltipParams) =>
          this.changeOrderSharedService.userFormatter(params.value),
        valueFormatter: (val: ValueFormatterParams) =>
          this.changeOrderSharedService.userFormatter(val.value),
      },
      {
        cellClass: ['grid-cell', 'ag-cell-align-left'],
        field: 'date_created',
        getQuickFilterText: Utils.agDateFormatter,
        headerName: 'Date Created',
        minWidth: 135,
        resizable: true,
        sort: 'desc',
        type: 'date',
        valueFormatter: Utils.agDateFormatter,
        width: 135,
      },
      {
        cellClass: ['grid-cell', 'ag-cell-align-left', 'text-elypsis'],
        field: 'created_by',
        getQuickFilterText: (params: GetQuickFilterTextParams) =>
          this.changeOrderSharedService.userFormatter(params.value),
        headerName: 'Created By',
        resizable: true,
        tooltipValueGetter: (params: ITooltipParams) =>
          this.changeOrderSharedService.userFormatter(params.value),
        valueFormatter: (val: ValueFormatterParams) =>
          this.changeOrderSharedService.userFormatter(val.value),
      },
    ],
    columnTypes: Utils.columnTypes,
    defaultColDef: {
      cellRenderer: AgCellWrapperComponent,
      minWidth: 80,
      resizable: false,
      suppressMenu: true,
      suppressMovable: true,
    },
    excelStyles: [
      ...Utils.generateExcelCurrencyStyles(Utils.CURRENCY_OPTIONS),
      ...AuxExcelStyles,
      {
        ...GetExcelStyle(AuxExcelStyleKeys.FIRST_ROW),
        borders: {
          borderBottom: {
            color: 'black',
            lineStyle: 'Continuous',
            weight: 1,
          },
        },
        id: 'trial_name',
      },
    ],
    suppressCellFocus: true,
  } as GridOptions);
  isAllCosSameCurrency = false;
  myChart: Chart | null = null;
  nameFilterValue = '';
  overlayNoRowsTemplate = TableConstants.NO_ROWS_CO_MESSAGE;
  selectedVendor = new UntypedFormControl('');
  showCOChart$ = new BehaviorSubject<boolean>(false);

  ngAfterViewInit(): void {
    runInInjectionContext(this.injector, () => {
      injectExecutableByRoute(() => {
        void this.openChangerOrderUpload(undefined, 'Add Change Order', 'Add');
      });
    });

    runInInjectionContext(this.injector, () => {
      injectExecutableByRoute(
        ({ modalVendorId }) => {
          void this.openChangerOrderUpload(modalVendorId);
        },
        undefined,
        'modalVendorId'
      );
    });
  }

  constructor() {
    this.changeOrderService.get().pipe(takeUntilDestroyed()).subscribe();
    this.organizationService.getListWithTotalBudgetAmount().pipe(takeUntilDestroyed()).subscribe();

    this.authService
      .isAuthorized$({
        permissions: [PermissionType.PERMISSION_TEMPLATE_UPLOADS],
        sysAdminsOnly: false,
      })
      .pipe(takeUntilDestroyed())
      .subscribe((permissionForDownload) => {
        this.userHasDownloadChangeOrderPermission$.next(permissionForDownload);
        this.gridAPI?.refreshCells();
      });

    this.organizationQuery.allVendors$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.selectedVendor.setValue('');
      this.organizationStore.setActive(null);
    });
    combineLatest([
      this.organizationQuery.selectActiveId(),
      this.launchDarklyService.select$((flags) => flags.section_change_order_chart),
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(([, showChart]) => {
        const chartLoading = this.coChartLoading$.getValue();

        this.showCOChart$.next(showChart);

        if (
          showChart &&
          !chartLoading &&
          (this.changeOrders.length || Object.keys(this.organizations).length)
        ) {
          void this.drawChart(
            this.organizations as { [id: string]: Partial<Organization> },
            this.changeOrders as Partial<ChangeOrder>[]
          );
        }
      });

    this.selectedBudgetCurrencyType$.pipe(takeUntilDestroyed()).subscribe();

    this.workflowService.getWorkflowList().pipe(takeUntilDestroyed()).subscribe();
  }

  async downloadChangeOrder(rowNode: RowNode) {
    const { co_id } = rowNode.data;

    const { data, errors, success } = await this.changeOrderQuery.downloadCO(co_id);

    if (success && data) {
      this.overlayService.success();
    } else {
      this.overlayService.error(errors);
    }
  }

  // eslint-disable-next-line max-lines-per-function, complexity
  async drawChart(
    organizations: { [id: string]: Partial<Organization> },
    changeOrders: Partial<ChangeOrder>[]
  ) {
    const loading = this.coChartLoading$.getValue();

    if (!loading) {
      this.coChartLoading$.next(true);
      const selectedVendorId = this.selectedVendor.value;
      const noVendorSelected = !selectedVendorId;

      const budgetVersionsToQuery: { [bv_id: string]: BvExpenseData } = {};
      const lreBudgetVersions: { [bv_id: string]: BudgetVersion } = {};
      const baselineBudgetVersions: { [bv_id: string]: BudgetVersion } = {};
      const changeOrderBudgetVersions: { [bv_id: string]: BudgetVersion } = {};

      for (const orgId of Object.keys(organizations)) {
        if (noVendorSelected || selectedVendorId === orgId) {
          const org = organizations[orgId];

          for (const bv of org.current_budget_versions || []) {
            if (
              Object.keys(budgetVersionsToQuery).indexOf(bv.budget_version_id) === -1 &&
              bv.budget_type === BudgetType.BUDGET_PRIMARY
            ) {
              // Budget Primary (A.K.A. "LRE") Should always "Win"
              budgetVersionsToQuery[bv.budget_version_id] = {
                ...bv,
                budget_short_type: 'LRE',
                expense_data: null,
              };
              lreBudgetVersions[bv.budget_version_id] = bv;
            }
          }

          if (
            org.baseline_budget_version &&
            Object.keys(budgetVersionsToQuery).indexOf(
              org.baseline_budget_version.budget_version_id
            ) === -1
          ) {
            baselineBudgetVersions[org.baseline_budget_version.budget_version_id] =
              org.baseline_budget_version;
            budgetVersionsToQuery[org.baseline_budget_version.budget_version_id] = {
              ...org.baseline_budget_version,
              budget_short_type: 'BASE',
              expense_data: null,
            };
          }
        }
      }

      // ONLY Loop through the COs after all Baseline and LRE budgets have been added
      for (const co of changeOrders) {
        const newBvId = co.merged_budget_version?.budget_version_id;

        if (
          co.merged_budget_version &&
          newBvId &&
          Object.keys(budgetVersionsToQuery).indexOf(newBvId) === -1 &&
          (noVendorSelected || co.organization?.id === selectedVendorId)
        ) {
          budgetVersionsToQuery[newBvId] = {
            ...co.merged_budget_version,
            budget_short_type: 'CO',
            expense_data: null,
          };
          changeOrderBudgetVersions[newBvId] = co.merged_budget_version;
        }
      }

      const promises: Promise<BvExpenseData | null>[] = [];

      for (const bv_id of Object.keys(budgetVersionsToQuery)) {
        if (budgetVersionsToQuery[bv_id].budget_version_id) {
          promises.push(this.getData(budgetVersionsToQuery[bv_id]));
        }
      }

      const unfiltered = await batchPromises(promises, (p) => p);
      const start_date = this.mainQuery.getAuxiliusStartDate();
      const QYFChart = `${new Date(start_date || '').getFullYear()}-Q${
        Math.floor(new Date(start_date || '').getMonth() / 3) + 1
      }`;
      const QYFChartFilter = `${
        Math.floor(new Date(start_date || '').getMonth() / 3) === 0
          ? Number(new Date(start_date || '').getFullYear()) - 1
          : Number(new Date(start_date || '').getFullYear())
      }-Q${
        Math.floor(new Date(start_date || '').getMonth() / 3) === 0
          ? 4
          : Math.floor(new Date(start_date || '').getMonth() / 3)
      }`;

      const dataFiltered = unfiltered.filter(
        (itm) => !(itm instanceof Error) && itm?.expense_data && itm?.expense_data?.length > 0
      ) as BvExpenseData[];

      const data = dataFiltered.map((x) => {
        const filteredExpense = x.expense_data?.filter((x) =>
          this.quarterDateCompare(QYFChartFilter, x.period || '')
        );

        const expenseData = filteredExpense?.filter((y) => {
          return this.quarterDateCompare(QYFChart, y?.period || '');
        });

        return { ...x, expense_data: expenseData };
      }) as BvExpenseData[];

      const labels = this.getLabels(
        data.map((d) => d?.expense_data) as { period: null | string }[][],
        QYFChartFilter
      );

      const datasets = this.transformData(noVendorSelected, data, labels);

      const ctx = document.getElementById('changeOrderLineChart');

      this.coChartLoading$.next(false);

      if (ctx) {
        if (this.myChart) {
          this.myChart.destroy();
        }

        this.myChart = new Chart(ctx as HTMLCanvasElement, {
          data: {
            datasets,
            labels,
          },
          options: {
            plugins: {
              legend: {
                display: true,
                labels: {
                  color: '#212332',
                  padding: 10,
                  usePointStyle: true,
                },
                position: 'bottom',
              },
              tooltip: {
                callbacks: {
                  label(tooltipItem) {
                    return `${tooltipItem.dataset.label} ${Utils.currencyFormatter(
                      tooltipItem.raw,
                      {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                      }
                    )}`;
                  },
                },
                intersect: false,
                mode: 'index',
              },
            },
            scales: {
              x: {
                ticks: {
                  autoSkip: false,
                },
              },
              y: {
                beginAtZero: true,
                position: 'right',
                ticks: {
                  callback: (value) => {
                    if (typeof value === 'number') {
                      return Utils.currencyFormatter(value, { notation: 'compact' });
                    }

                    return value;
                  },
                },
              },
            },
          },
          type: 'line',
        });
      }
    }
  }

  async getData(budgetVersion: Partial<BvExpenseData>) {
    const { data } = await firstValueFrom(
      this.gqlService.listBudgetVersionExpenses$(budgetVersion.budget_version_id || '')
    );

    return {
      ...budgetVersion,
      budget_short_type: budgetVersion.budget_short_type,
      budget_version_id: budgetVersion.budget_version_id || '',
      expense_data: data,
    };
  }

  getDynamicExcelParams = (): ExcelExportParams => {
    const trial = this.mainQuery.getSelectedTrial();
    const columnKeys =
      this.gridAPI
        .getColumnDefs()
        ?.filter((colDef) => {
          const asColDef = colDef as ColDef;

          return (
            asColDef.colId !== 'co_id' &&
            asColDef.colId !== 'vendor_id' &&
            asColDef.colId !== 'actions'
          );
        })
        .reduce<string[]>((accum, cell: ColDef | ColGroupDef) => {
          const asColDef = cell as ColDef;

          accum.push(asColDef.colId || '');

          return accum;
        }, []) || [];
    const dateStr = dayjs(new Date()).format('YYYY.MM.DD-HHmmss');
    const fileName = trial?.short_name
      ? `${trial?.short_name}_change-orders_${dateStr}.xlsx`
      : `_Total Budget_${dateStr}.xlsx`;
    const appendContent: ExcelExportParams['appendContent'] = this.isAllCosSameCurrency
      ? [
          {
            cells: [
              {
                data: { type: 'String', value: `Total` },
                mergeAcross: 1,
                styleId: 'total_row_header',
              },
              {
                data: { type: 'Number', value: `${this.totalCosts.total}` },
                styleId: `total_row_${this.allOrgCurrency}`,
              },
              {
                data: { type: 'Number', value: `${this.totalCosts.variance}` },
                styleId: `total_row_${this.allOrgCurrency}`,
              },
              {
                data: { type: 'Number', value: `` },
                mergeAcross: 4,
                styleId: `total_row`,
              },
            ],
          },
        ]
      : [{ cells: [] }];

    return {
      ...this.excelOptions,
      appendContent: appendContent,
      columnKeys: columnKeys,
      fileName: fileName,
      prependContent: [
        {
          cells: [
            {
              data: { type: 'String', value: `Trial: ${trial?.name}` },
              mergeAcross: columnKeys.length - 1,
              styleId: 'first_row',
            },
          ],
        },
      ],
    } as ExcelExportParams;
  };

  getLabels(data: { period: null | string }[][], qString = '') {
    const labelSet = new Set<string>();

    for (const d of data) {
      for (const expense of d) {
        if (expense.period) {
          let label = this.transformPeriodStr(expense.period) || '';

          if (qString?.split('-')?.[1] === expense.period) {
            label = qString.split('-')[1] || '';
          }

          if (label) {
            labelSet.add(label);
          }
        }
      }
    }

    const labelArr = Array.from(labelSet) as string[];

    // Sort by quarters
    return labelArr.sort((a, b) => {
      const x = a === qString.split('-')[1] ? this.transformPeriodStr(qString) : a;
      const y = b === qString.split('-')[1] ? this.transformPeriodStr(qString) : b;

      const year1 = parseInt(x.split(' ')?.[1], 10) || -1;
      const year2 = parseInt(y.split(' ')?.[1], 10) || -1;

      if (year1 !== year2) {
        return year1 > year2 ? 1 : -1;
      }

      return x > y ? 1 : -1;
    });
  }

  goToCODetail(event: CellClickedEvent) {
    const id = event.data?.co_id;

    if (id) {
      void this.router.navigateByUrl(
        `${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.CHANGE_ORDER.INDEX}/${id}`
      );
    }
  }

  mergeExpenseData(expenseData: listBudgetVersionExpensesQuery[][], sortedLabels: string[]) {
    // The labels _must_ stay in order
    const ed: number[] = new Array(sortedLabels.length).fill(0);

    for (const singleBudgetData of expenseData) {
      for (const expD of singleBudgetData) {
        const label = this.transformPeriodStr(expD.period || '');
        const labelIdx = sortedLabels.indexOf(label);

        if (label && labelIdx >= 0 && expD.amount) {
          ed[labelIdx] += expD.amount;
        }
      }
    }

    return ed;
  }

  onFirstDataRendered() {
    this.gridAPI.sizeColumnsToFit();
  }

  onGridReady({ api }: GridReadyEvent) {
    this.gridAPI = api;
    this.gridAPI$.next(api);

    if (this.selectedVendor.value) {
      this.onOrganizationSelected(this.selectedVendor.value);
    }
  }

  onOrganizationSelected(orgId: string) {
    this.organizationStore.setActive(orgId);
    const filterInstance = this.gridAPI.getFilterInstance('vendor_id');

    if (filterInstance) {
      filterInstance.setModel(orgId ? { values: [orgId] } : null);
    }

    this.gridAPI.onFilterChanged();

    if (this.gridAPI.getDisplayedRowCount() === 0) {
      this.gridAPI.showNoRowsOverlay();
    } else {
      this.gridAPI.hideOverlay();
    }
  }

  async openChangerOrderUpload(
    vendorId?: string,
    header = 'Upload Change Order',
    primaryButtonLabel = 'Upload'
  ) {
    const resp = this.overlayService.openPopup({
      data: <ChangeOrderUploadData>{ vendorId },
      modal: ChangeOrderUploadComponent,
      settings: {
        header,
        primaryButton: { label: primaryButtonLabel },
      },
    });
    const event = await firstValueFrom(resp.afterClosed$);

    if (event.data) {
      if (this.organizationQuery.getActive()?.id === this.selectedVendor.value) {
        this.onOrganizationSelected(this.selectedVendor.value);
      }
    }
  }

  quarterDateCompare(x: string, y: string) {
    const xQuarter = x.split('-Q');
    const yQuarter = y.split('-Q');

    if (Number(xQuarter[0]) > Number(yQuarter[0])) {
      return false;
    }

    if (Number(xQuarter[0]) === Number(yQuarter[0])) {
      return Number(xQuarter[1]) <= Number(yQuarter[1]);
    }

    return true;
  }

  async removeChangeOrder(rowNode: RowNode) {
    if (!rowNode.data) {
      return;
    }

    const co = this.changeOrderQuery.getEntity(rowNode.data.co_id);

    if (!co) {
      return;
    }

    const resp = this.overlayService.openPopup<
      { message: string },
      null | { reason: string },
      ConfirmationWithReasonModalComponent
    >({
      data: {
        message: `Are you sure you want to remove Change Order ${rowNode.data.co_number}?`,
      },
      modal: ConfirmationWithReasonModalComponent,
      settings: {
        header: 'Remove Change Order',
        primaryButton: {
          label: 'Remove',
        },
      },
    });

    const event = await firstValueFrom(resp.afterClosed$);

    if (event.data?.reason) {
      await this.changeOrderService.remove(co, event.data.reason);
    }
  }

  runningSum(nums: number[]) {
    let total = 0;

    return nums.map((num) => (total += num));
  }

  // eslint-disable-next-line max-lines-per-function
  transformData(noVendorSelected: boolean, data: BvExpenseData[], sortedLabels: string[]) {
    const lre_color = '#094673';
    const co_color = '#bacad0';
    const baseline_color = '#236262';
    const lineTension = 0.5;

    const lres = data.filter((bv) => bv.budget_short_type === 'LRE');
    const baselines = data.filter((bv) => bv.budget_short_type === 'BASE');
    const changeorders = data.filter((bv) => bv.budget_short_type === 'CO');

    const datasets: ChartDataset<'line'>[] = [];

    if (lres.length > 0) {
      const lre_expense_data = lres
        .filter(
          (lre) =>
            lre.expense_data &&
            lre.expense_data.filter((ed) => ed.amount !== null && ed.amount !== undefined)
        )
        .map((lre) => lre.expense_data);
      const x = this.mergeExpenseData(
        lre_expense_data as listBudgetVersionExpensesQuery[][],
        sortedLabels
      );
      const lre_data = this.runningSum(x);

      datasets.push({
        cubicInterpolationMode: 'monotone',
        data: lre_data,
        label: noVendorSelected ? 'Current (LRE)' : `Current (LRE) Budget (${lres[0].budget_name})`,
        pointBackgroundColor: lre_color,
        pointBorderColor: lre_color,
        pointHoverBackgroundColor: lre_color,
        pointHoverBorderColor: lre_color,
        segment: {
          backgroundColor: lre_color,
          borderColor: lre_color,
        },
        tension: lineTension,
      });
    }

    if (changeorders.length > 0 && !noVendorSelected) {
      for (const co of changeorders) {
        const x = co.expense_data?.map((ed) => ed.amount || 0) || [];

        datasets.push({
          cubicInterpolationMode: 'monotone',
          data: this.runningSum(x),
          label: co.budget_name || '',
          pointBackgroundColor: co_color,
          pointBorderColor: co_color,
          pointHoverBackgroundColor: co_color,
          pointHoverBorderColor: co_color,
          segment: {
            backgroundColor: co_color,
            borderColor: co_color,
          },
          tension: lineTension,
        });
      }
    }

    if (baselines.length > 0) {
      const baseline_expense_data = baselines
        .filter(
          (baseln) =>
            baseln.expense_data &&
            baseln.expense_data.filter((ed) => ed.amount !== null && ed.amount !== undefined)
        )
        .map((baseln) => baseln.expense_data);
      const x = this.mergeExpenseData(
        baseline_expense_data as listBudgetVersionExpensesQuery[][],
        sortedLabels
      );
      const baseline_totals = this.runningSum(x);

      datasets.push({
        cubicInterpolationMode: 'monotone',
        data: baseline_totals,
        label: 'Baseline',
        pointBackgroundColor: baseline_color,
        pointBorderColor: baseline_color,
        pointHoverBackgroundColor: baseline_color,
        pointHoverBorderColor: baseline_color,
        segment: {
          backgroundColor: baseline_color,
          borderColor: baseline_color,
        },
        tension: lineTension,
      });
    }

    return datasets;
  }

  transformPeriodStr(period: string) {
    if (period) {
      const split = period.split('-');

      if (split.length > 1) {
        return `${split[1]} ${split[0]}`;
      }

      if (split.length === 1) {
        return `${split[0]}`;
      }
    }

    return '';
  }
}
